import { useState, useEffect, useMemo } from 'react';
import { PAYMENT_METHODS } from "../../../../constants/financial";
import { getMethodLabel } from '../constants';

export const usePaymentForm = ({
    acquirers = [],
    suggestedValue = 0,
    onAddPayment
}) => {
    const [paymentMethod, setPaymentMethod] = useState(PAYMENT_METHODS.CASH);

    const [paymentData, setPaymentData] = useState({
        value: '',
        installments: '1',
        provider: '',
        brand: '',
        auth: '',
        netValue: 0
    });

    useEffect(() => {
        if (typeof suggestedValue === 'number') {
            setPaymentData(prev => ({ ...prev, value: suggestedValue.toFixed(2) }));
            if (suggestedValue === 0) {
                setPaymentMethod(PAYMENT_METHODS.CASH);
            }
        }
    }, [suggestedValue]);

    const availableBrands = useMemo(() => {
        if (!paymentData.provider || !acquirers.length) return [];
        const selectedAcquirer = acquirers.find(a => a.id === paymentData.provider);
        if (!selectedAcquirer || !selectedAcquirer.rates) return [];
        return Object.keys(selectedAcquirer.rates);
    }, [paymentData.provider, acquirers]);

    const feeBreakdown = useMemo(() => {
        const val = parseFloat(paymentData.value) || 0;
        if (!paymentData.provider || !paymentData.brand || !acquirers.length || val <= 0) return null;
        if (paymentMethod !== PAYMENT_METHODS.CREDIT_CARD && paymentMethod !== PAYMENT_METHODS.DEBIT_CARD) return null;

        const acquirer = acquirers.find(a => a.id === paymentData.provider);
        if (!acquirer?.rates) return null;

        const brandConfig = acquirer.rates[paymentData.brand];
        if (!brandConfig) return null;

        let baseRatePercent = 0;
        const installments = parseInt(paymentData.installments) || 1;

        if (paymentMethod === PAYMENT_METHODS.DEBIT_CARD) {
            baseRatePercent = parseFloat(brandConfig.debit) || 0;
        } else {
            const creditRates = brandConfig.credit || [];
            const matchingRate = creditRates.find(c => parseInt(c.installments) === installments);
            baseRatePercent = matchingRate ? parseFloat(matchingRate.rate) || 0 : 0;
        }

        // 1. Desconto Base do Cartão
        const baseDiscount = val * (baseRatePercent / 100);
        let anticipationDiscount = 0;

        // 2. Desconto da Antecipação
        if (
            paymentMethod === PAYMENT_METHODS.CREDIT_CARD &&
            acquirer.anticipation?.enabled === true &&
            acquirer.anticipation?.type === "automatic" &&
            installments > 1
        ) {
            const feePerMonthPercent = parseFloat(acquirer.anticipation.feePerMonth) || 0;
            const installmentValue = val / installments;

            for (let i = 1; i < installments; i++) {
                anticipationDiscount += installmentValue * (feePerMonthPercent / 100) * i;
            }
        }

        const totalDiscount = baseDiscount + anticipationDiscount;
        const netValue = val - totalDiscount;
        const effectiveRatePercent = val > 0 ? (totalDiscount / val) * 100 : 0;

        return {
            effectiveRatePercent: Number(effectiveRatePercent.toFixed(2)),
            totalDiscount: Number(totalDiscount.toFixed(2)),
            netValue: Number(netValue.toFixed(2)),
            hasAnticipation: anticipationDiscount > 0
        };
    }, [paymentData, acquirers, paymentMethod]);

    const estimatedFee = feeBreakdown ? feeBreakdown.effectiveRatePercent : null;
    const netValueDisplay = feeBreakdown ? feeBreakdown.netValue : null;

    useEffect(() => {
        if (availableBrands.length > 0 && !availableBrands.includes(paymentData.brand)) {
            setPaymentData(prev => ({ ...prev, brand: availableBrands[0] }));
        }
    }, [availableBrands, paymentData.brand]);

    const handleInputChange = (field, value) => {
        setPaymentData(prev => ({ ...prev, [field]: value }));
    };

    const handleAddPaymentClick = () => {
        const providerName = acquirers.find(a => a.id === paymentData.provider)?.name || paymentData.provider;

        onAddPayment({
            methodId: paymentMethod,
            methodLabel: getMethodLabel(paymentMethod),
            ...paymentData,
            netValue: netValueDisplay || parseFloat(paymentData.value) || 0,
            providerName
        });
        setPaymentData({
            value: '',
            installments: '1',
            provider: '',
            brand: '',
            auth: '',
            netValue: 0
        });
    };

    return {
        paymentMethod,
        setPaymentMethod,
        paymentData,
        handleInputChange,
        availableBrands,
        estimatedFee,
        netValueDisplay,
        handleAddPaymentClick
    };
};
